import { isFresh } from "./dateUtils";
import { isOfflineLikeStatus, normalizeStatusValue } from "./statusUtils";

export const WATCH_ONLINE_THRESHOLD_SEC = 15;

export function normalizeBatteryValue(value) {
  if (value === null || value === undefined || value === "") return null;

  const num = Number(value);
  if (!Number.isFinite(num)) return null;

  const rounded = Math.round(num);
  if (rounded < 0 || rounded > 100) return null;

  return rounded;
}

export function normalizeDeviceTypeValue(value) {
  const v = String(value || "")
    .trim()
    .toUpperCase();

  if (v === "MINTWATCH") return "MINTWATCH";

  if (v === "MOBILE_CCTV" || v === "MOBILE-CCTV" || v === "MOBILECCTV") {
    return "MOBILE_CCTV";
  }

  if (v === "MINTCAM") return "MINTCAM";

  return "MINTCAM";
}

export function deviceTypeBadgeClass(type) {
  const v = normalizeDeviceTypeValue(type);

  if (v === "MOBILE_CCTV") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300";
  }

  if (v === "MINTCAM") {
    return "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300";
  }

  return "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300";
}

export function deviceTypeText(type) {
  const v = normalizeDeviceTypeValue(type);

  if (v === "MOBILE_CCTV") return "이동형 CCTV";
  if (v === "MINTCAM") return "민트캠";
  if (v === "MINTWATCH") return "민트워치";

  return "-";
}

export function getCameraStatusLabel(liveStatus) {
  const v = normalizeStatusValue(liveStatus);

  if (v === "ONLINE") return "ONLINE";
  if (v === "DISABLED") return "DISABLED";

  return "OFFLINE";
}

export function getWatchStatusLabel(row) {
  const serverStatus =
    normalizeStatusValue(row?.status) ||
    normalizeStatusValue(row?.liveStatus) ||
    normalizeStatusValue(row?.deviceStatus);

  if (serverStatus) {
    return serverStatus;
  }

  const hasSos = !!(row?.sos || row?.isSos);
  if (hasSos) return "ALERT";

  return isFresh(
    row?.updatedAt ||
      row?.UpdatedAt ||
      row?.timestamp ||
      row?.Timestamp ||
      row?.createdAt ||
      row?.CreatedAt ||
      row?.ts,
    WATCH_ONLINE_THRESHOLD_SEC
  )
    ? "ONLINE"
    : "OFFLINE";
}

export function getBatteryText(value, status) {
  if (isOfflineLikeStatus(status)) return "-";

  const battery = normalizeBatteryValue(value);
  return battery === null ? "-" : `${battery}%`;
}

export function getHeartRateText(value, status) {
  if (isOfflineLikeStatus(status)) return "-";
  if (value === null || value === undefined || value === "") return "-";

  const num = Number(value);
  return Number.isFinite(num) ? `${num} bpm` : "-";
}

export function getGpsText(row) {
  if (isOfflineLikeStatus(row?.status)) return "-";

  const lat = row?.gps?.lat ?? row?.gpsLat ?? row?.lat ?? null;
  const lng = row?.gps?.lng ?? row?.gpsLng ?? row?.lng ?? null;

  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return "-";
  }

  const latNum = Number(lat);
  const lngNum = Number(lng);

  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return "-";

  return `${latNum.toFixed(5)}, ${lngNum.toFixed(5)}`;
}

export function getStreamStatusText(row) {
  const deviceType = normalizeDeviceTypeValue(row?.deviceType);

  if (deviceType !== "MINTCAM" && deviceType !== "MOBILE_CCTV") return "-";

  const v = String(row?.streamStatus || "").toUpperCase();

  if (v === "ONLINE") return "정상 송출";
  if (v === "DISABLED") return "비활성";

  return "미송출";
}

export function getOwnerText(row) {
  const deviceType = normalizeDeviceTypeValue(row?.deviceType);

  if (deviceType === "MINTCAM" || deviceType === "MOBILE_CCTV") {
    return row?.ownerName || row?.camNoText || "-";
  }

  return row?.ownerName || row?.siteId || "-";
}

export function getSafeLastSeenForDisplay(item) {
  return (
    item?.lastSeen ||
    item?.lastHeartbeatAt ||
    item?.updatedAt ||
    item?.UpdatedAt ||
    item?.createdAt ||
    item?.CreatedAt ||
    null
  );
}

export function normalizeCameraDevices(cameras, statuses) {
  const statusMap = new Map(
    (Array.isArray(statuses) ? statuses : []).map((item) => [
      String(item?.camIdx ?? item?.camNo ?? item?.streamKey ?? ""),
      item,
    ])
  );

  return (Array.isArray(cameras) ? cameras : []).map((cam) => {
    const key = String(cam?.camIdx ?? cam?.camNo ?? cam?.streamKey ?? "");
    const matchedStatus = statusMap.get(key);

    const liveStatus = getCameraStatusLabel(matchedStatus?.liveStatus);
    const camNoText = cam?.camNo ? `카메라 ${cam.camNo}` : "-";

    const deviceType = normalizeDeviceTypeValue(
      cam?.deviceType ||
        cam?.sourceType ||
        matchedStatus?.deviceType ||
        matchedStatus?.sourceType
    );

    const safeLastSeen =
      matchedStatus?.updatedAt ||
      matchedStatus?.lastHeartbeatAt ||
      matchedStatus?.UpdatedAt ||
      matchedStatus?.createdAt ||
      matchedStatus?.CreatedAt ||
      null;

    const safeBattery =
      liveStatus === "ONLINE"
        ? normalizeBatteryValue(
            matchedStatus?.battery ??
              matchedStatus?.Battery ??
              matchedStatus?.deviceBattery ??
              matchedStatus?.phoneBattery
          )
        : null;

    return {
      rowKey: `cam_${cam?.camIdx ?? key}`,
      deviceType,
      id: cam?.camIdx ?? key,
      deviceId: cam?.camIdx ?? key,
      camNo: cam?.camNo ?? null,
      name:
        cam?.name || cam?.camTitle || camNoText || deviceTypeText(deviceType),
      ownerName: camNoText,
      camNoText,
      siteId: "",
      status: liveStatus,
      lastSeen: safeLastSeen,
      battery: safeBattery,
      heartRate: null,
      gps:
        matchedStatus?.latitude !== null &&
        matchedStatus?.latitude !== undefined &&
        matchedStatus?.longitude !== null &&
        matchedStatus?.longitude !== undefined
          ? {
              lat: Number(matchedStatus.latitude),
              lng: Number(matchedStatus.longitude),
              accuracy:
                matchedStatus.locationAccuracy ?? null,
              updatedAt: matchedStatus.locationUpdatedAt ?? null,
            }
          : null,
      gpsLat: matchedStatus?.latitude ?? null,
      gpsLng: matchedStatus?.longitude ?? null,
      streamStatus: liveStatus,
      raw: {
        camera: cam,
        status: matchedStatus || null,
      },
    };
  });
}

export function normalizeWatchDevices(watches) {
  return (Array.isArray(watches) ? watches : []).map((row, index) => {
    const updatedAt =
      row?.updatedAt ||
      row?.UpdatedAt ||
      row?.timestamp ||
      row?.Timestamp ||
      row?.createdAt ||
      row?.CreatedAt ||
      row?.ts ||
      null;

    const status = getWatchStatusLabel(row);

    return {
      rowKey: `watch_${row?.deviceId || row?.DeviceId || index}`,
      deviceType: "MINTWATCH",
      id: row?.deviceId || row?.DeviceId || row?.id || `watch_${index}`,
      deviceId: row?.deviceId || row?.DeviceId || row?.id || "",
      name:
        row?.deviceName ||
        row?.DeviceName ||
        row?.name ||
        row?.deviceId ||
        row?.DeviceId ||
        "민트워치",
      ownerName:
        row?.workerName ||
        row?.WorkerName ||
        row?.userName ||
        row?.UserName ||
        row?.siteId ||
        row?.SiteId ||
        "-",
      siteId: row?.siteId || row?.SiteId || "",
      status,
      lastSeen: updatedAt,
      battery:
        status === "ONLINE" || status === "ALERT"
          ? normalizeBatteryValue(row?.battery ?? row?.Battery)
          : null,
      heartRate: row?.heartRate ?? row?.HeartRate ?? null,
      gps: row?.gps || null,
      gpsLat: row?.gpsLat ?? row?.GpsLat ?? row?.lat ?? null,
      gpsLng: row?.gpsLng ?? row?.GpsLng ?? row?.lng ?? null,
      streamStatus: null,
      raw: row,
    };
  });
}